import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import AutomaticStateTransitionGeneralDataCard from './AutomaticStateTransitionGeneralDataCard';
import { initialState, reducer } from 'features/automaticStateTransition/reducers/generalData/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { automaticStateTransitionValidator } from 'features/automaticStateTransition/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ACTIVE_DOCUMENT_TYPES, DOCUMENT_TYPE_WITH_ALLOWED_STATES } from '../../../queries';
import { AUTOMATICSTATETRANSITION_SAVE } from '../../../mutations';
import { AUTOMATICSTATETRANSITION_SAVE_SUBSCRIPTION } from '../../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const AutomaticStateTransitionGeneralDataContainer = ({ automaticStateTransitionId, data, onRedirect }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [automaticStateTransitionValidaton, validateAutomaticStateTransition] = useValidation(automaticStateTransitionValidator);

  // #region Queries

  const { data: activeDocumentTypesData } = useQuery(ACTIVE_DOCUMENT_TYPES);

  const { data: documentTypeWithAllowedStates } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES, {
    variables: {
      documentTypeWithAllowedStatesId: localState.header.documentTypeId
    },
    skip: !localState?.header?.documentTypeId
  });

  // #endregion

  // #region Mutations

  const [AutomaticStateTransitionSave] = useMutation(AUTOMATICSTATETRANSITION_SAVE);

  // #endregion

  // #region Subscriptions

  useSubscription(AUTOMATICSTATETRANSITION_SAVE_SUBSCRIPTION, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AutomaticStateTransitions.AutomaticStateTransitionSaved'), 'success');
      if (!automaticStateTransitionId)
        onRedirect(data?.automaticStateTransitionUpdated?.automaticStateTransition?.automaticStateTransitionId);
      else
        client.cache.evict({
          id: `AutomaticStateTransition:{"automaticStateTransitionId":${automaticStateTransitionId}}`
        });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'automaticStateTransitions' });
      client.cache.gc();
    }
  });

  // #endregion

  // #region Handlers

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    if (!validateAutomaticStateTransition(localState.header)) return;
    // eslint-disable-next-line no-unused-vars
    const { changed, ...automaticStateTransition } = localState.header;

    dispatch({ type: 'OnHeaderSaved' });
    return AutomaticStateTransitionSave({ variables: { input: { ...automaticStateTransition } } });
  };

  // #endregion

  // #region Side effects

  useEffect(() => {
    validateAutomaticStateTransition(localState.header);
  }, [localState.header, validateAutomaticStateTransition]);

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  // #endregion

  return (
    <AutomaticStateTransitionGeneralDataCard
      automaticStateTransition={localState.header}
      docTypes={activeDocumentTypesData?.activeDocumentTypes || []}
      states={documentTypeWithAllowedStates?.documentTypeWithAllowedStates}
      validation={automaticStateTransitionValidaton}
      onSave={handleSave}
      onPropertyChanged={handlePropertyChanged}
    />
  );
};

AutomaticStateTransitionGeneralDataContainer.propTypes = {
  automaticStateTransitionId: PropTypes.number,
  data: PropTypes.object,
  onRedirect: PropTypes.func
};

export default AutomaticStateTransitionGeneralDataContainer;
